import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import type {
  Guid,
  IEquipmentAssignmentInfo,
  IEquipmentMaintenanceInfo,
  IEquipmentModelInfo,
  IEquipmentTypeInfo,
  IEquipmentUsageMetricInfo,
  IAgencyInfo,
  IDocumentInfo,
  IDriverInfo,
  IEntityStatusWorkflowInfo,
  IMaintenanceTypeInfo,
  IResponse,
  IThirdPartEnterpriseInfo,
  IEquipmentVehicleContraventionInfo,
  IEquipmentVehicleInfo,
} from "@kiraah-shared/common";
import {
  EquipmentAssignment,
  EquipmentMaintenance,
  EquipmentUsageMetric,
  Driver,
  LvfAppModule,
  LvfOperationType,
  LvfRequestSource,
  SearchFilterBase,
  EquipmentVehicle,
  EquipmentVehicleContravention,
} from "@kiraah-shared/common";
import { ENVIRONMENT } from "@kiraah-shared/environment";
import { Observable, mergeMap, of, throwError } from "rxjs";
import { MOBILE_GATEWAY_URL } from "../../auth/mobile-auth.config";
import { MobileAuthStore } from "../../state/mobile-auth.store";
import { MobileProfileStore } from "../../state/mobile-profile.store";

/**
 * Client HTTP parc équipement (équipements + kilométrages + assignations + maintenances
 * + contraventions + chauffeurs + types de maintenance + agences), aligné sur les
 * services web {@link VehicleService}, {@link EquipmentUsageMetricService},
 * {@link EquipmentAssignmentService}, {@link EquipmentMaintenanceService},
 * {@link VehicleContraventionService}, {@link DriverService},
 * {@link MaintenanceTypeService}, {@link AgencyService} sans ServiceBase ni
 * DialogService (réservés au web).
 */
@Injectable({ providedIn: "root" })
export class MobileFleetApiClient {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(MobileAuthStore);
  private readonly profileStore = inject(MobileProfileStore);
  private readonly environment = inject(ENVIRONMENT);

  private readonly equipmentsUrl: string;
  private readonly equipmentVehiclesUrl: string;
  private readonly equipmentTypesUrl: string;
  private readonly equipmentModelsUrl: string;
  private readonly equipmentUsageMetricsUrl: string;
  private readonly equipmentAssignmentsUrl: string;
  private readonly equipmentMaintenancesUrl: string;
  private readonly equipmentContraventionsUrl: string;
  private readonly driversUrl: string;
  private readonly maintenanceTypesUrl: string;
  private readonly agenciesUrl: string;
  private readonly thirdPartEnterprisesUrl: string;
  private readonly entityStatusWorkflowsUrl: string;
  private readonly documentsUrl: string;

  constructor(@Inject(MOBILE_GATEWAY_URL) gatewayUrl: string) {
    const root = `${gatewayUrl}/api`;
    this.equipmentsUrl = `${root}/supplychain/equipments`;
    this.equipmentVehiclesUrl = `${root}/supplychain/equipmentvehicles`;
    this.equipmentTypesUrl = `${root}/supplychain/equipmenttypes`;
    this.equipmentModelsUrl = `${root}/supplychain/equipmentmodels`;
    this.equipmentUsageMetricsUrl = `${root}/supplychain/equipmentusagemetrics`;
    this.equipmentAssignmentsUrl = `${root}/supplychain/equipmentassignments`;
    this.equipmentMaintenancesUrl = `${root}/supplychain/equipmentmaintenances`;
    this.equipmentContraventionsUrl = `${root}/supplychain/equipmentcontraventions`;
    this.driversUrl = `${root}/core/drivers`;
    this.maintenanceTypesUrl = `${root}/supplychain/equipmentmaintenancetypes`;
    this.agenciesUrl = `${root}/core/agencies`;
    this.thirdPartEnterprisesUrl = `${root}/core/thirdparts`;
    this.entityStatusWorkflowsUrl = `${root}/core/entitystatusworkflows`;
    this.documentsUrl = `${root}/core/documents`;
  }

  /**
   * Récupère le workflow de statuts pour un module donné, équivalent de
   * {@link EntityStatusWorkflowService.getByLvfAppModule} (web). Utilisé pour
   * peupler le sélecteur de statut quand l'entité ne porte pas de
   * `WorkflowUid` (cas observé sur certains tenants).
   */
  getEntityStatusWorkflowByModule(lvfAppModule: LvfAppModule): Observable<IEntityStatusWorkflowInfo | null> {
    return this.http
      .get<IResponse<IEntityStatusWorkflowInfo>>(`${this.entityStatusWorkflowsUrl}/module/${lvfAppModule}`, {
        headers: this.lvfHeaders(LvfAppModule.ENTITY_STATUS, LvfOperationType.READ),
      })
      .pipe(mergeMap((response) => of(response?.IsSuccessful && response.Data ? response.Data : null)));
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Équipements
  // ─────────────────────────────────────────────────────────────────────────────

  /** Liste parc — aligné web {@link EquipmentService} (`supplychain/equipments/_filter`, enrichissement conducteur / maintenance). */
  filterEquipmentVehicles(filter: SearchFilterBase): Observable<IEquipmentVehicleInfo[]> {
    return this.postFilter<IEquipmentVehicleInfo>(`${this.equipmentsUrl}/_filter`, filter, LvfAppModule.EQUIPMENT, "vehicles_filter_failed");
  }

  /** Détail — aligné web {@link EquipmentService.get} (`supplychain/equipments/{uid}`). */
  getEquipmentVehicle(equipmentUid: Guid): Observable<IEquipmentVehicleInfo> {
    return this.getOne<IEquipmentVehicleInfo>(`${this.equipmentsUrl}/${equipmentUid}`, LvfAppModule.EQUIPMENT, "vehicle_get_failed");
  }

  /** Aligné {@link EquipmentTypeService} (`supplychain/equipmenttypes/_filter`). */
  filterEquipmentTypes(filter: SearchFilterBase): Observable<IEquipmentTypeInfo[]> {
    return this.postFilter<IEquipmentTypeInfo>(
      `${this.equipmentTypesUrl}/_filter`,
      filter,
      LvfAppModule.EQUIPMENT_TYPE,
      "equipment_types_filter_failed",
    );
  }

  filterEquipmentModels(filter: SearchFilterBase): Observable<IEquipmentModelInfo[]> {
    return this.postFilter<IEquipmentModelInfo>(
      `${this.equipmentModelsUrl}/_filter`,
      filter,
      LvfAppModule.EQUIPMENT_MODEL,
      "vehicle_models_filter_failed",
    );
  }

  updateEquipmentVehicle(equipment: EquipmentVehicle): Observable<IEquipmentVehicleInfo> {
    return this.putOne<IEquipmentVehicleInfo, EquipmentVehicle>(
      `${this.equipmentVehiclesUrl}/${equipment.Uid}`,
      equipment,
      LvfAppModule.EQUIPMENT,
      "vehicle_update_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Kilométrages
  // ─────────────────────────────────────────────────────────────────────────────

  filterEquipmentUsageMetrics(filter: SearchFilterBase): Observable<IEquipmentUsageMetricInfo[]> {
    return this.postFilter<IEquipmentUsageMetricInfo>(
      `${this.equipmentUsageMetricsUrl}/_filter`,
      filter,
      LvfAppModule.EQUIPMENT_USAGE_METRIC,
      "vehicle_mileages_filter_failed",
    );
  }

  getEquipmentUsageMetric(mileageUid: Guid): Observable<IEquipmentUsageMetricInfo> {
    return this.getOne<IEquipmentUsageMetricInfo>(
      `${this.equipmentUsageMetricsUrl}/${mileageUid}`,
      LvfAppModule.EQUIPMENT_USAGE_METRIC,
      "vehicle_mileage_get_failed",
    );
  }

  createEquipmentUsageMetric(row: EquipmentUsageMetric): Observable<IEquipmentUsageMetricInfo> {
    return this.postOne<IEquipmentUsageMetricInfo, EquipmentUsageMetric>(
      this.equipmentUsageMetricsUrl,
      row,
      LvfAppModule.EQUIPMENT_USAGE_METRIC,
      "vehicle_mileage_create_failed",
    );
  }

  updateEquipmentUsageMetric(row: EquipmentUsageMetric): Observable<IEquipmentUsageMetricInfo> {
    return this.putOne<IEquipmentUsageMetricInfo, EquipmentUsageMetric>(
      `${this.equipmentUsageMetricsUrl}/${row.Uid}`,
      row,
      LvfAppModule.EQUIPMENT_USAGE_METRIC,
      "vehicle_mileage_update_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Assignations
  // ─────────────────────────────────────────────────────────────────────────────

  filterEquipmentAssignments(filter: SearchFilterBase): Observable<IEquipmentAssignmentInfo[]> {
    return this.postFilter<IEquipmentAssignmentInfo>(
      `${this.equipmentAssignmentsUrl}/_filter`,
      filter,
      LvfAppModule.EQUIPMENT_ASSIGNMENT,
      "vehicle_assignments_filter_failed",
    );
  }

  getEquipmentAssignment(uid: Guid): Observable<IEquipmentAssignmentInfo> {
    return this.getOne<IEquipmentAssignmentInfo>(
      `${this.equipmentAssignmentsUrl}/${uid}`,
      LvfAppModule.EQUIPMENT_ASSIGNMENT,
      "vehicle_assignment_get_failed",
    );
  }

  createEquipmentAssignment(row: EquipmentAssignment): Observable<IEquipmentAssignmentInfo> {
    return this.postOne<IEquipmentAssignmentInfo, EquipmentAssignment>(
      this.equipmentAssignmentsUrl,
      row,
      LvfAppModule.EQUIPMENT_ASSIGNMENT,
      "vehicle_assignment_create_failed",
    );
  }

  updateEquipmentAssignment(row: EquipmentAssignment): Observable<IEquipmentAssignmentInfo> {
    return this.putOne<IEquipmentAssignmentInfo, EquipmentAssignment>(
      `${this.equipmentAssignmentsUrl}/${row.Uid}`,
      row,
      LvfAppModule.EQUIPMENT_ASSIGNMENT,
      "vehicle_assignment_update_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Maintenances
  // ─────────────────────────────────────────────────────────────────────────────

  filterEquipmentMaintenances(filter: SearchFilterBase): Observable<IEquipmentMaintenanceInfo[]> {
    return this.postFilter<IEquipmentMaintenanceInfo>(
      `${this.equipmentMaintenancesUrl}/_filter`,
      filter,
      LvfAppModule.EQUIPMENT_MAINTENANCE,
      "vehicle_maintenances_filter_failed",
    );
  }

  getEquipmentMaintenance(uid: Guid): Observable<IEquipmentMaintenanceInfo> {
    return this.getOne<IEquipmentMaintenanceInfo>(
      `${this.equipmentMaintenancesUrl}/${uid}`,
      LvfAppModule.EQUIPMENT_MAINTENANCE,
      "vehicle_maintenance_get_failed",
    );
  }

  createEquipmentMaintenance(row: EquipmentMaintenance): Observable<IEquipmentMaintenanceInfo> {
    return this.postOne<IEquipmentMaintenanceInfo, EquipmentMaintenance>(
      this.equipmentMaintenancesUrl,
      row,
      LvfAppModule.EQUIPMENT_MAINTENANCE,
      "vehicle_maintenance_create_failed",
    );
  }

  updateEquipmentMaintenance(row: EquipmentMaintenance): Observable<IEquipmentMaintenanceInfo> {
    return this.putOne<IEquipmentMaintenanceInfo, EquipmentMaintenance>(
      `${this.equipmentMaintenancesUrl}/${row.Uid}`,
      row,
      LvfAppModule.EQUIPMENT_MAINTENANCE,
      "vehicle_maintenance_update_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Contraventions
  // ─────────────────────────────────────────────────────────────────────────────

  filterEquipmentContraventions(filter: SearchFilterBase): Observable<IEquipmentVehicleContraventionInfo[]> {
    return this.postFilter<IEquipmentVehicleContraventionInfo>(
      `${this.equipmentContraventionsUrl}/_filter`,
      filter,
      LvfAppModule.DRIVER_TRAFFIC_TICKET,
      "vehicle_contraventions_filter_failed",
    );
  }

  getEquipmentContravention(uid: Guid): Observable<IEquipmentVehicleContraventionInfo> {
    return this.getOne<IEquipmentVehicleContraventionInfo>(
      `${this.equipmentContraventionsUrl}/${uid}`,
      LvfAppModule.DRIVER_TRAFFIC_TICKET,
      "vehicle_contravention_get_failed",
    );
  }

  createEquipmentContravention(row: EquipmentVehicleContravention): Observable<IEquipmentVehicleContraventionInfo> {
    return this.postOne<IEquipmentVehicleContraventionInfo, EquipmentVehicleContravention>(
      this.equipmentContraventionsUrl,
      row,
      LvfAppModule.DRIVER_TRAFFIC_TICKET,
      "vehicle_contravention_create_failed",
    );
  }

  updateEquipmentContravention(row: EquipmentVehicleContravention): Observable<IEquipmentVehicleContraventionInfo> {
    return this.putOne<IEquipmentVehicleContraventionInfo, EquipmentVehicleContravention>(
      `${this.equipmentContraventionsUrl}/${row.Uid}`,
      row,
      LvfAppModule.DRIVER_TRAFFIC_TICKET,
      "vehicle_contravention_update_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Chauffeurs
  // ─────────────────────────────────────────────────────────────────────────────

  filterDrivers(filter: SearchFilterBase): Observable<IDriverInfo[]> {
    return this.postFilter<IDriverInfo>(
      `${this.driversUrl}/_filter`,
      filter,
      LvfAppModule.NONE,
      "drivers_filter_failed",
    );
  }

  /**
   * Aligné sur `DriverService` du web qui passe `LvfAppModule.NONE` à
   * `ServiceBase`. Avec `LvfAppModule.DRIVER`, le back rejetait le `GET /{uid}`
   * (l'écran mobile n'arrivait pas à charger un chauffeur).
   */
  getDriver(uid: Guid): Observable<IDriverInfo> {
    return this.getOne<IDriverInfo>(`${this.driversUrl}/${uid}`, LvfAppModule.NONE, "driver_get_failed");
  }

  createDriver(driver: Driver): Observable<IDriverInfo> {
    return this.postOne<IDriverInfo, Driver>(this.driversUrl, driver, LvfAppModule.NONE, "driver_create_failed");
  }

  updateDriver(driver: Driver): Observable<IDriverInfo> {
    return this.putOne<IDriverInfo, Driver>(
      `${this.driversUrl}/${driver.Uid}`,
      driver,
      LvfAppModule.NONE,
      "driver_update_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Catalogues secondaires (sélecteurs détail)
  // ─────────────────────────────────────────────────────────────────────────────

  filterMaintenanceTypes(filter: SearchFilterBase): Observable<IMaintenanceTypeInfo[]> {
    return this.postFilter<IMaintenanceTypeInfo>(
      `${this.maintenanceTypesUrl}/_filter`,
      filter,
      LvfAppModule.MAINTENANCE_TYPE,
      "maintenance_types_filter_failed",
    );
  }

  filterAgencies(filter: SearchFilterBase): Observable<IAgencyInfo[]> {
    return this.postFilter<IAgencyInfo>(
      `${this.agenciesUrl}/_filter`,
      filter,
      LvfAppModule.AGENCY,
      "agencies_filter_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Documents associés (équivalent web `DocumentService.getAll` filtré sur
  // `AssociatedEntityUids` + `LvfDocumentAppModules`, comme `ngx-document-compliance`).
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Liste les documents liés à une entité équipement (équipement, chauffeur, assignation,
   * contravention, maintenance, …) via `POST /core/documents/_filter`.
   * Le module passé en `X-APP-MODULE-UID` est celui du module documents de l'entité,
   * comme côté web (`DocumentService.setLvfAppModule(config.DocumentLvfAppModule)`).
   */
  filterAssociatedDocuments(entityUid: Guid, documentLvfAppModule: LvfAppModule): Observable<IDocumentInfo[]> {
    const filter = SearchFilterBase.CreateInstance();
    filter.Paginate = false;
    filter.AssociatedEntityUids = [entityUid];
    filter.LvfDocumentAppModules = [documentLvfAppModule];
    return this.http
      .post<IResponse<IDocumentInfo[]>>(`${this.documentsUrl}/_filter`, filter, {
        headers: this.lvfHeaders(documentLvfAppModule, LvfOperationType.READ_ALL),
      })
      .pipe(
        mergeMap((response) => {
          if (!response?.IsSuccessful) {
            return throwError(() => new Error(response?.MessageKey ?? response?.Message ?? "documents_filter_failed"));
          }
          return of(response.Data ?? []);
        }),
      );
  }

  /** Téléchargement binaire d’un document (équivalent `DocumentService.downloadContent`). */
  downloadAssociatedDocument(documentUid: Guid, documentLvfAppModule: LvfAppModule): Observable<Blob> {
    return this.http.get(`${this.documentsUrl}/${documentUid}/content`, {
      headers: this.lvfHeaders(documentLvfAppModule, LvfOperationType.READ),
      responseType: "blob",
    });
  }

  filterThirdPartEnterprises(filter: SearchFilterBase): Observable<IThirdPartEnterpriseInfo[]> {
    return this.postFilter<IThirdPartEnterpriseInfo>(
      `${this.thirdPartEnterprisesUrl}/_filter`,
      filter,
      LvfAppModule.THIRD_PART_ENTERPRISE,
      "third_part_enterprises_filter_failed",
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers HTTP génériques
  // ─────────────────────────────────────────────────────────────────────────────

  private postFilter<T>(
    url: string,
    filter: SearchFilterBase,
    module: LvfAppModule,
    errorKey: string,
  ): Observable<T[]> {
    return this.http
      .post<IResponse<T[]>>(url, filter, { headers: this.lvfHeaders(module, LvfOperationType.READ_ALL) })
      .pipe(
        mergeMap((response) => {
          if (!response?.IsSuccessful) {
            return throwError(() => new Error(response?.MessageKey ?? response?.Message ?? errorKey));
          }
          return of(response.Data ?? []);
        }),
      );
  }

  private getOne<T>(url: string, module: LvfAppModule, errorKey: string): Observable<T> {
    return this.http
      .get<IResponse<T>>(url, { headers: this.lvfHeaders(module, LvfOperationType.READ) })
      .pipe(
        mergeMap((response) => {
          if (!response?.IsSuccessful || response.Data == null) {
            return throwError(() => new Error(response?.MessageKey ?? response?.Message ?? errorKey));
          }
          return of(response.Data);
        }),
      );
  }

  private postOne<TResp, TBody>(
    url: string,
    body: TBody,
    module: LvfAppModule,
    errorKey: string,
  ): Observable<TResp> {
    return this.http
      .post<IResponse<TResp>>(url, body, { headers: this.lvfHeaders(module, LvfOperationType.CREATE) })
      .pipe(
        mergeMap((response) => {
          if (!response?.IsSuccessful || response.Data == null) {
            return throwError(() => new Error(response?.MessageKey ?? response?.Message ?? errorKey));
          }
          return of(response.Data);
        }),
      );
  }

  private putOne<TResp, TBody>(
    url: string,
    body: TBody,
    module: LvfAppModule,
    errorKey: string,
  ): Observable<TResp> {
    return this.http
      .put<IResponse<TResp>>(url, body, { headers: this.lvfHeaders(module, LvfOperationType.UPDATE) })
      .pipe(
        mergeMap((response) => {
          if (!response?.IsSuccessful || response.Data == null) {
            return throwError(() => new Error(response?.MessageKey ?? response?.Message ?? errorKey));
          }
          return of(response.Data);
        }),
      );
  }

  private lvfHeaders(module: LvfAppModule, operation: LvfOperationType): HttpHeaders {
    const identityUid = this.authStore.authResult()?.ProfilData?.Identity?.Uid ?? "";
    const appUid = this.profileStore.activeApplication()?.Uid ?? "APP-EMPTY-UID";
    return new HttpHeaders({
      "Content-Type": "application/json",
      "X-CONNECTED-IDENTITY-UID": `${identityUid}`,
      "X-USER-TOKEN-UID": "",
      "X-APP-UID": `${appUid}`,
      "X-APP-MODULE-UID": `${module}`,
      "X-REQUEST-SRC": `${LvfRequestSource.MOBILE}`,
      "X-OPERATION-TYPE": `${operation}`,
      "X-WEB-APP-URL": `${this.environment.WebAppUrl ?? ""}`,
    });
  }
}
